import { toaster } from '@/components/ui/toaster';
import {
  billingSettingsQueryKey,
  fetchBillingSettings,
  readBillingSettings,
} from '@/hooks/queries/use-billing-settings-query';
import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  OrganizationBillingPortalData,
  OrganizationBillingSettingsData,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { BillingTargetChangedError } from './use-billing-action-scope';
import { useBillingSettingsScope } from './use-billing-settings-scope';

const portalSchema = z
  .object({
    organizationId: z.string().min(1),
    brandId: z.string().min(1),
    url: z
      .string()
      .url()
      .refine((value) => {
        const url = new URL(value);
        return (
          value === value.trim() &&
          url.protocol === 'https:' &&
          url.hostname === 'billing.stripe.com' &&
          !url.username &&
          !url.password &&
          !url.port
        );
      }),
  })
  .strict();

/** Opening a portal establishes neither a payment nor a confirmed card. */
export function useOpenBillingPortal(snapshot?: OrganizationBillingSettingsData) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { scope, assertCurrent } = useBillingSettingsScope();
  const key = billingSettingsQueryKey(scope);
  return useMutation<
    OrganizationBillingPortalData,
    AxiosError<ApiResponse> | Error,
    void,
    typeof scope
  >({
    mutationKey: [...key, 'portal'],
    retry: false,
    onMutate: () => {
      assertCurrent(scope);
      return scope;
    },
    mutationFn: async () => {
      assertCurrent(scope);
      const known = readBillingSettings(snapshot);
      if (
        !known?.canManage ||
        !known.portalAvailable ||
        known.organizationId !== scope.organizationId ||
        known.brandId !== scope.brandId
      )
        throw new Error('No authorized billing portal');
      const fresh = await client.fetchQuery({
        queryKey: key,
        queryFn: ({ signal }) => fetchBillingSettings(scope, assertCurrent, signal),
        staleTime: 0,
        retry: false,
      });
      assertCurrent(scope);
      if (!fresh.canManage || !fresh.portalAvailable) throw new Error('Billing portal unavailable');
      const response = await api.post<ApiResponse<unknown>>('/dashboard/billing/settings/portal');
      assertCurrent(scope);
      const parsed = portalSchema.safeParse(response.data.data);
      if (
        !response.data.success ||
        !parsed.success ||
        parsed.data.organizationId !== scope.organizationId ||
        parsed.data.brandId !== scope.brandId
      )
        throw new Error('Invalid billing portal response');
      const confirmed = await client.fetchQuery({
        queryKey: key,
        queryFn: ({ signal }) => fetchBillingSettings(scope, assertCurrent, signal),
        staleTime: 0,
        retry: false,
      });
      assertCurrent(scope);
      if (!confirmed.canManage || !confirmed.portalAvailable)
        throw new BillingTargetChangedError('Billing portal permission changed');
      return parsed.data;
    },
    onSuccess: (data, _request, target) => {
      if (!target) return;
      assertCurrent(target);
      window.location.assign(data.url);
    },
    onError: (error, _request, target) => {
      if (error instanceof BillingTargetChangedError || !target) return;
      try {
        assertCurrent(target);
      } catch {
        return;
      }
      toaster.error({ title: t('common.error'), description: t('billing.settings.portalError') });
    },
  });
}
